import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Clock, DollarSign, Users, TrendingUp, AlertTriangle, Check, Sparkles } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { formatCurrency, formatNumber } from '../context/CalculatorContext';

// Calculate effectiveness based on hours
function getEffectiveness(hours) {
  if (hours <= 20) {
    return (hours / 60) * 100;
  } else if (hours <= 35) {
    return 30 + ((hours - 20) / 40) * 40;
  } else if (hours <= 55) {
    return 70 + ((hours - 35) / 25) * 20;
  } else {
    return Math.min(100, 90 + ((hours - 60) / 20) * 10);
  }
}

// Get zone info based on hours
function getZoneInfo(hours) {
  if (hours <= 20) {
    return {
      zone: 'red',
      label: 'Insuficiente',
      message: `Con ${hours} hrs/mes obtienes resultados parciales e inconsistentes. Un profesional dedica 60 hrs/mes: monitoreo diario, optimización semanal, análisis y estrategia. Con menos de 20 hrs, el algoritmo trabaja sin guía y el presupuesto se drena en clics de baja calidad.`,
      bgColor: 'bg-red-50',
      borderColor: 'border-red-300',
      textColor: 'text-red-800',
    };
  } else if (hours <= 35) {
    return {
      zone: 'orange',
      label: 'Parcial',
      message: `Con ${hours} hrs/mes puedes hacer el seguimiento básico, pero sin optimización semanal completa. Las campañas pierden eficiencia progresivamente. Un perfil no especializado en esta zona suele tomar decisiones con datos incompletos.`,
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-300',
      textColor: 'text-amber-800',
    };
  } else if (hours <= 55) {
    return {
      zone: 'lightgreen',
      label: 'Aceptable',
      message: `Con ${hours} hrs/mes te acercas a una gestión aceptable, aunque sin la velocidad de reacción ni el expertise de un equipo especializado. La curva de aprendizaje y los errores de configuración siguen siendo factores de riesgo.`,
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-300',
      textColor: 'text-emerald-800',
    };
  } else {
    return {
      zone: 'green',
      label: 'Profesional',
      message: `Con ${hours} hrs/mes estás en el rango de gestión profesional. La pregunta es: ¿ese tiempo es realmente tuyo para dedicar? Y más importante: ¿tienes el expertise para que esas horas produzcan los mismos resultados que un equipo especializado?`,
      bgColor: 'bg-green-50',
      borderColor: 'border-green-300',
      textColor: 'text-green-800',
    };
  }
}

export function DIYSimulator({ presupuestoMensual, ingresosProyectados, leadsNecesarios, ticket }) {
  // State for simulator controls
  const [horasMensuales, setHorasMensuales] = useState(40);
  const [valorHora, setValorHora] = useState(300);
  const [porcentajeDesperdicio, setPorcentajeDesperdicio] = useState(30);
  const [igualaMensual, setIgualaMensual] = useState(8000);

  // Calculations
  const diyCalculations = useMemo(() => {
    // Effectiveness based on hours
    const efectividad = getEffectiveness(horasMensuales) / 100;
    
    // Cost of opportunity (time)
    const costoOportunidad = horasMensuales * valorHora;
    
    // Wasted budget
    const presupuestoPerdido = presupuestoMensual * (porcentajeDesperdicio / 100);
    
    // Real leads obtained DIY
    const leadsRealesDIY = Math.round(leadsNecesarios * (1 - porcentajeDesperdicio / 100) * efectividad);
    
    // Real revenue DIY
    const ingresosRealesDIY = leadsRealesDIY * ticket * (efectividad); // Simplified: leads * ticket * effectiveness
    
    // Real ROAS DIY
    const roasRealDIY = presupuestoMensual > 0 ? ingresosRealesDIY / presupuestoMensual : 0;
    
    // Marketing intensity DIY
    const intensidadDIY = ingresosRealesDIY > 0 ? (presupuestoMensual / ingresosRealesDIY) * 100 : 0;
    
    // Total real cost DIY = presupuesto + costo de oportunidad + presupuesto perdido
    const costoTotalDIY = presupuestoMensual + costoOportunidad + presupuestoPerdido;
    
    // --- Vitamina Online ---
    // ROAS with iguala included
    const roasVitamina = (presupuestoMensual + igualaMensual) > 0 
      ? ingresosProyectados / (presupuestoMensual + igualaMensual) 
      : 0;
    
    // Marketing intensity Vitamina
    const intensidadVitamina = ingresosProyectados > 0 
      ? ((presupuestoMensual + igualaMensual) / ingresosProyectados) * 100 
      : 0;
    
    // Total cost Vitamina
    const costoTotalVitamina = presupuestoMensual + igualaMensual;
    
    // Savings calculation: DIY total cost vs Vitamina total cost
    // ahorro positivo = contratar agencia es mejor
    const ahorro = costoTotalDIY - costoTotalVitamina;
    
    // Suggested iguala if negative
    const igualaSugerida = Math.round((costoTotalDIY * 0.9) / 500) * 500;

    return {
      efectividad: efectividad * 100,
      costoOportunidad,
      presupuestoPerdido,
      leadsRealesDIY,
      ingresosRealesDIY,
      roasRealDIY,
      intensidadDIY,
      costoTotalDIY,
      roasVitamina,
      intensidadVitamina,
      costoTotalVitamina,
      ahorro,
      igualaSugerida,
    };
  }, [horasMensuales, valorHora, porcentajeDesperdicio, igualaMensual, presupuestoMensual, ingresosProyectados, leadsNecesarios, ticket]);

  const zoneInfo = getZoneInfo(horasMensuales);

  return (
    <div className="mt-8 pt-8 border-t-2 border-slate-200">
      {/* Header */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-6"
      >
        <h2 className="text-xl md:text-2xl font-bold text-slate-900 mb-2">
          ¿Y si lo haces tú mismo? — simulador de costo real
        </h2>
      </motion.div>

      {/* Section 1 - Intro */}
      <div className="mb-8 p-6 bg-amber-50 border-l-4 border-amber-500 rounded-r-xl">
        <p className="text-amber-800 leading-relaxed">
          El costo de hacer publicidad sin agencia no es solo el presupuesto desperdiciado. También es el tiempo del dueño que deja de usarse en lo que genera dinero. Ponle precio a ese tiempo y verás que la iguala se paga sola.
        </p>
      </div>

      {/* Main Grid - Two Panels */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Left Panel - Hours and Time Value */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
          <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
            <Clock className="h-5 w-5 text-emerald-600" />
            Tu tiempo invertido
          </h3>

          {/* Hours Slider */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-semibold text-slate-700">Horas/mes que piensas dedicar</label>
              <span className="text-2xl font-bold text-emerald-700">{horasMensuales} hrs</span>
            </div>
            
            {/* Slider with markers */}
            <div className="relative pt-8 pb-2">
              {/* Markers */}
              <div className="absolute top-0 left-0 right-0 flex justify-between text-xs">
                <span className="text-slate-400">5</span>
                <div className="absolute" style={{ left: `${((40 - 5) / 75) * 100}%` }}>
                  <div className="w-px h-4 bg-emerald-400"></div>
                  <span className="text-emerald-600 text-[10px] whitespace-nowrap">mín. recomendado</span>
                </div>
                <div className="absolute" style={{ left: `${((60 - 5) / 75) * 100}%` }}>
                  <div className="w-px h-4 bg-emerald-700"></div>
                  <span className="text-emerald-700 text-[10px] whitespace-nowrap">gestión profesional</span>
                </div>
                <span className="text-slate-400">80</span>
              </div>
              
              <Slider
                value={[horasMensuales]}
                onValueChange={(value) => setHorasMensuales(value[0])}
                min={5}
                max={80}
                step={5}
                className="mt-2"
              />
            </div>

            {/* Zone bar */}
            <div className="flex h-6 rounded-lg overflow-hidden text-[10px] font-medium mt-4">
              <div className="bg-red-400 text-white flex items-center justify-center" style={{ width: '20%' }}>
                Insuficiente
              </div>
              <div className="bg-amber-400 text-white flex items-center justify-center" style={{ width: '15%' }}>
                Parcial
              </div>
              <div className="bg-emerald-400 text-white flex items-center justify-center" style={{ width: '25%' }}>
                Aceptable
              </div>
              <div className="bg-emerald-600 text-white flex items-center justify-center" style={{ width: '40%' }}>
                Profesional
              </div>
            </div>
            <div className="flex text-[9px] text-slate-500 mt-1">
              <span style={{ width: '20%' }} className="text-center">5–20 hrs</span>
              <span style={{ width: '15%' }} className="text-center">25–35 hrs</span>
              <span style={{ width: '25%' }} className="text-center">40–55 hrs</span>
              <span style={{ width: '40%' }} className="text-center">60–80 hrs</span>
            </div>

            {/* Dynamic message */}
            <div className={`mt-4 p-4 rounded-xl ${zoneInfo.bgColor} border ${zoneInfo.borderColor}`}>
              <p className={`text-sm ${zoneInfo.textColor} leading-relaxed`}>
                {zoneInfo.message}
              </p>
            </div>
          </div>

          {/* Hour Value Input */}
          <div className="mb-6">
            <label className="text-sm font-semibold text-slate-700 block mb-2">Valor de tu hora (MXN)</label>
            <div className="relative max-w-xs">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
              <Input
                type="number"
                value={valorHora}
                onChange={(e) => setValorHora(Number(e.target.value) || 0)}
                step={50}
                className="h-12 pl-8 font-mono"
              />
            </div>
          </div>

          {/* Opportunity Cost Card */}
          <div className="bg-emerald-700 text-white rounded-xl p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-emerald-200 text-sm">Costo de oportunidad mensual</p>
                <p className="text-xs text-emerald-300 mt-1">{horasMensuales} hrs × {formatCurrency(valorHora)}/hr</p>
              </div>
              <p className="text-2xl font-bold font-mono">{formatCurrency(diyCalculations.costoOportunidad)}</p>
            </div>
          </div>
        </div>

        {/* Right Panel - Waste and Iguala */}
        <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6">
          <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-slate-600" />
            Desperdicio e iguala
          </h3>

          {/* Waste Slider */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-semibold text-slate-700">% presupuesto desperdiciado sin optimización</label>
              <span className="text-2xl font-bold text-red-600">{porcentajeDesperdicio}%</span>
            </div>
            
            <Slider
              value={[porcentajeDesperdicio]}
              onValueChange={(value) => setPorcentajeDesperdicio(value[0])}
              min={15}
              max={45}
              step={5}
              className="mt-2"
            />
            
            <p className="text-xs text-slate-500 mt-3">
              25–40% del presupuesto se pierde sin monitoreo activo: pujas sin ajustar, palabras negativas omitidas, audiencias saturadas, anuncios sin rotación.
            </p>
          </div>

          {/* Iguala Input */}
          <div className="mb-6">
            <label className="text-sm font-semibold text-slate-700 block mb-2">Iguala mensual Vitamina Online</label>
            <div className="relative max-w-xs">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-medium">$</span>
              <Input
                type="number"
                value={igualaMensual}
                onChange={(e) => setIgualaMensual(Number(e.target.value) || 0)}
                step={500}
                className="h-12 pl-8 font-mono"
              />
            </div>
            <p className="text-xs text-slate-500 mt-2">
              Gestión completa: estrategia, ejecución, optimización semanal, reportes y atención a cuenta.
            </p>
          </div>

          {/* Wasted Budget Card */}
          <div className="bg-red-100 border border-red-200 rounded-xl p-4 mb-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-red-700 text-sm font-medium">Presupuesto desperdiciado</p>
                <p className="text-xs text-red-600 mt-1">{formatCurrency(presupuestoMensual)} × {porcentajeDesperdicio}%</p>
              </div>
              <p className="text-2xl font-bold font-mono text-red-700">{formatCurrency(diyCalculations.presupuestoPerdido)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Section 3 - Savings Banner */}
      <div className={`mb-8 p-6 rounded-2xl ${diyCalculations.ahorro >= 0 ? 'bg-blue-50 border border-blue-200' : 'bg-red-50 border border-red-200'}`}>
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <p className={`font-semibold ${diyCalculations.ahorro >= 0 ? 'text-blue-800' : 'text-red-800'}`}>
              Diferencia real: hacerlo solo vs contratar a Vitamina Online
            </p>
            <p className={`text-sm ${diyCalculations.ahorro >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
              (presupuesto + costo de tu tiempo + presupuesto perdido) − (presupuesto + iguala)
            </p>
          </div>
          <div className={`text-3xl md:text-4xl font-bold font-mono ${diyCalculations.ahorro >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
            {diyCalculations.ahorro >= 0 ? '+' : ''}{formatCurrency(diyCalculations.ahorro)}
          </div>
        </div>
      </div>

      {/* Section 4 - Comparison Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Left Column - DIY */}
        <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-6">
          <h3 className="font-bold text-amber-800 mb-4 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Sin agencia
          </h3>
          
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-amber-200">
              <span className="text-slate-600">Tu tiempo al mes</span>
              <span className="font-semibold text-slate-900">{horasMensuales} hrs</span>
            </div>
            <div className="flex justify-between py-2 border-b border-amber-200">
              <span className="text-slate-600">Efectividad estimada vs profesional</span>
              <span className="font-semibold text-amber-700">{diyCalculations.efectividad.toFixed(0)}%</span>
            </div>
            <div className="flex justify-between py-2 border-b border-amber-200">
              <span className="text-slate-600">Costo de oportunidad (tiempo)</span>
              <span className="font-semibold font-mono text-slate-900">{formatCurrency(diyCalculations.costoOportunidad)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-amber-200">
              <span className="text-slate-600">% presupuesto desperdiciado</span>
              <span className="font-semibold text-red-600">{porcentajeDesperdicio}%</span>
            </div>
            <div className="flex justify-between py-2 border-b border-amber-200">
              <span className="text-slate-600">Presupuesto perdido</span>
              <span className="font-semibold font-mono text-red-600">{formatCurrency(diyCalculations.presupuestoPerdido)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-amber-200">
              <span className="text-slate-600">Leads reales obtenidos</span>
              <span className="font-semibold text-slate-900">{formatNumber(diyCalculations.leadsRealesDIY)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-amber-200">
              <span className="text-slate-600">ROAS real</span>
              <span className="font-semibold text-slate-900">{diyCalculations.roasRealDIY.toFixed(1)}x</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-600">Intensidad del marketing</span>
              <span className="font-semibold text-slate-900">{diyCalculations.intensidadDIY > 100 ? '>100' : diyCalculations.intensidadDIY.toFixed(1)}%</span>
            </div>
          </div>

          {/* Total DIY Card */}
          <div className="mt-4 bg-red-700 text-white rounded-xl p-4">
            <p className="text-red-200 text-sm">Costo real total mensual</p>
            <p className="text-2xl font-bold font-mono mt-1">{formatCurrency(diyCalculations.costoTotalDIY)}</p>
            <p className="text-xs text-red-300 mt-1">Presupuesto + costo de oportunidad + presupuesto perdido</p>
          </div>
        </div>

        {/* Right Column - Vitamina Online */}
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-2xl p-6">
          <h3 className="font-bold text-emerald-800 mb-4 flex items-center gap-2">
            <Check className="h-5 w-5" />
            Con Vitamina Online
          </h3>
          
          <div className="space-y-3">
            <div className="flex justify-between py-2 border-b border-emerald-200">
              <span className="text-slate-600">Tu tiempo al mes</span>
              <div className="text-right">
                <span className="font-semibold text-emerald-700">0 hrs</span>
                <p className="text-xs text-slate-500">Supervisión: 2-4 hrs mensuales</p>
              </div>
            </div>
            <div className="flex justify-between py-2 border-b border-emerald-200">
              <span className="text-slate-600">Horas del equipo en tu cuenta</span>
              <span className="font-semibold text-emerald-700">~60 hrs</span>
            </div>
            <div className="flex justify-between py-2 border-b border-emerald-200">
              <span className="text-slate-600">Costo de oportunidad (tiempo)</span>
              <span className="font-semibold font-mono text-emerald-700">$0</span>
            </div>
            <div className="flex justify-between py-2 border-b border-emerald-200">
              <span className="text-slate-600">% presupuesto desperdiciado</span>
              <span className="font-semibold text-emerald-700">~0%</span>
            </div>
            <div className="flex justify-between py-2 border-b border-emerald-200">
              <span className="text-slate-600">Iguala mensual</span>
              <span className="font-semibold font-mono text-slate-900">{formatCurrency(igualaMensual)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-emerald-200">
              <span className="text-slate-600">Leads obtenidos</span>
              <span className="font-semibold text-slate-900">{formatNumber(leadsNecesarios)}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-emerald-200">
              <span className="text-slate-600">ROAS con iguala incluida</span>
              <span className="font-semibold text-slate-900">{diyCalculations.roasVitamina.toFixed(1)}x</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-600">Intensidad del marketing</span>
              <span className="font-semibold text-slate-900">{diyCalculations.intensidadVitamina.toFixed(1)}%</span>
            </div>
          </div>

          {/* Total Vitamina Card */}
          <div className="mt-4 bg-emerald-700 text-white rounded-xl p-4">
            <p className="text-emerald-200 text-sm">Costo real total mensual</p>
            <p className="text-2xl font-bold font-mono mt-1">{formatCurrency(diyCalculations.costoTotalVitamina)}</p>
            <p className="text-xs text-emerald-300 mt-1">Presupuesto + iguala</p>
          </div>
        </div>
      </div>

      {/* Section 5 - Dynamic Verdict */}
      <div className={`mb-8 p-6 rounded-2xl border-2 ${diyCalculations.ahorro >= 0 ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`}>
        {diyCalculations.ahorro >= 0 ? (
          <div className="flex items-start gap-4">
            <Check className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-1" />
            <p className="text-emerald-800 leading-relaxed">
              Contratar a Vitamina Online cuesta <strong>{formatCurrency(igualaMensual + presupuestoMensual)}</strong>/mes (presupuesto + iguala). 
              Hacerlo tú con <strong>{horasMensuales} hrs/mes</strong> (efectividad estimada: <strong>{diyCalculations.efectividad.toFixed(0)}%</strong>) 
              cuesta <strong>{formatCurrency(diyCalculations.costoTotalDIY)}</strong>/mes entre presupuesto 
              (<strong>{formatCurrency(presupuestoMensual)}</strong>), presupuesto desperdiciado 
              (<strong>{formatCurrency(diyCalculations.presupuestoPerdido)}</strong>) y el valor de tu tiempo 
              (<strong>{formatCurrency(diyCalculations.costoOportunidad)}</strong>). 
              La diferencia es <strong>{formatCurrency(diyCalculations.ahorro)}</strong> a favor de contratar la agencia — 
              sin contar que obtienes <strong>{formatNumber(leadsNecesarios)}</strong> leads vs <strong>{formatNumber(diyCalculations.leadsRealesDIY)}</strong> leads, 
              y un ROAS de <strong>{diyCalculations.roasVitamina.toFixed(1)}x</strong> vs <strong>{diyCalculations.roasRealDIY.toFixed(1)}x</strong>.
            </p>
          </div>
        ) : (
          <div className="flex items-start gap-4">
            <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
            <p className="text-amber-800 leading-relaxed">
              Con los valores actuales, el costo con Vitamina Online (<strong>{formatCurrency(presupuestoMensual + igualaMensual)}</strong>) es menor que el costo real 
              de hacerlo solo (<strong>{formatCurrency(diyCalculations.costoTotalDIY)}</strong>), pero la diferencia calculada muestra <strong>{formatCurrency(Math.abs(diyCalculations.ahorro))}</strong> en contra. 
              Prueba aumentar las horas del slider o el valor por hora del dueño para ver el impacto real de tu tiempo.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
